/**
 * เลือกหัวข้อ — ธีม "หนังสือเรียน" (D18 · mockup docs/ui-book-theme.html)
 *
 * แถบวิชาอยู่บนสุด (วิชาปัจจุบันมีขอบ) → การ์ด "ทำทั้งวิชา" (หรือ "ทำหัวข้อที่เลือก") พร้อมปุ่ม
 * จำนวนข้อ 5/10/20 ที่กดแล้วเริ่มทันที → แถบคั่นสาระ (`section`) → การ์ดหน่วย
 * ★ D21 — ปุ่มที่สี่ "ทั้งหมด (N ข้อ)" = ทุกข้อในขอบเขต (หัวข้อลูก / แม่รวมลูก / ทั้งวิชา) ครบ ไม่ซ้ำ
 *   ปุ่ม 5/10/20 ที่ ≥ N ถูกซ่อน (ทั้งหมดครอบไว้แล้ว) · N > 30 มีข้อความ "ใช้เวลาประมาณ X นาที"
 *   (45 วินาที/ข้อ ปัดขึ้นเป็นหลัก 5 นาที) ใต้ปุ่ม ไม่มี dialog
 * ★ D22 — "การ์ดชุดรวม" ของหัวข้อแม่ที่มีลูก (groups_as_subtopics · 0 ข้อผูกตรง) วางเหนือแถบแรกของมัน
 *   **เฉพาะเมื่อ** จำนวนข้อรวมของแม่ < จำนวนข้อทั้งวิชา (วิทย์ ม.1: BO2 104 < 154 เพราะมี seed 50 ปน)
 *   เท่ากัน (สังคม/ไทย ป.4) = ไม่แสดง เพราะการ์ดวิชาให้ตัวเลขเดียวกันอยู่แล้ว · ปุ่มกด = QuizCreate ด้วย
 *   topic_id ของแม่ (เซิร์ฟเวอร์รวมลูกให้ · ทั้งหมด = ครบ ไม่ซ้ำ) · การ์ดวิชายังเป็นทั้งวิชาตาม D21
 *
 * ★ กติกาเดิมทั้งหมดยังอยู่: ตัวเลขจาก API · หัวข้อที่เลือกเดี่ยวไม่ได้ต้องยังเห็น (D9) ·
 *   หัวข้อแม่นับรวมลูก (D17) · เด็กถูกกรองชั้นที่เซิร์ฟเวอร์ · โหมดฝึก/สอบเลือกได้เหมือนเดิม
 * ★ แถบความคืบหน้าของการ์ด = ผลรอบล่าสุดของผู้เรียนคนนี้ในหัวข้อนั้น (รวมลูก) ไม่มี = "ยังไม่เคยทำ"
 *   100% = "แม่นแล้ว" — ไม่มีดาว ไม่มีนับถอยหลังสอบ (ยังไม่มีข้อมูลจริง ตัดออกตามที่ตกลง)
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Center, Shell } from "@/components/Shell";
import { translate as t } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import {
  bandsOf,
  bundleUnits,
  colorClass,
  estimateMinutes,
  unitTab,
  useBookSkin,
} from "@/lib/book";
import { useSession } from "@/lib/session";
import type { QuizOut, SubjectOut, TopicNode, TopicTree } from "@/lib/types";

import { SubjectStrip } from "./SubjectPage";

const COUNTS = [5, 10, 20];
const ALL = "all";
const ESTIMATE_FROM = 30;

function flatten(nodes: TopicNode[]): TopicNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children)]);
}

export function TopicPage(): JSX.Element {
  useBookSkin();
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const learnerId = useSession(
    (s) => s.learnerId ?? s.me?.learners[0]?.id ?? null,
  );
  // ผู้ปกครอง (ไม่มี active_learner) ส่งชั้นของลูกคนแรก · เด็กถูกกรองที่เซิร์ฟเวอร์เอง
  const me = useSession((s) => s.me);
  const kid = me?.active_learner ?? me?.learners[0] ?? null;
  const kidGrade = kid?.grade ?? null;
  const gradeQ =
    me && !me.active_learner && kidGrade ? `?grade=${kidGrade}&` : "?";

  const [mode, setMode] = useState<"practice" | "exam">("practice");
  const [picked, setPicked] = useState<string | null>(null);
  const wholeRef = useRef<HTMLDivElement>(null);

  /**
   * ★ เลือกหัวข้อแล้วต้องเลื่อนจอกลับไปที่การ์ดบนสุด (24 ก.ย. 2569)
   *
   * CK ทดสอบเองแล้วเจอกับดัก: เด็กเลื่อนลงไปแตะการ์ดหัวข้อที่ต้องการ การ์ดติดไฮไลต์
   * ถูกต้อง แต่**ปุ่มจำนวนข้อของหัวข้อนั้นอยู่ที่การ์ดบนสุดซึ่งพ้นจอไปแล้ว** สิ่งที่อยู่
   * ตรงหน้าเด็กคือการ์ดชุดรวมที่มีปุ่ม 5/10/20 ครบ → กดอันนั้นเป็นธรรมชาติที่สุด
   * ผลคือได้ชุดคละทั้งวิชาแทนที่จะได้หัวข้อที่ตั้งใจ และไม่เคยได้ซ้อมหัวข้อนั้นเลย
   * (นี่คือต้นเหตุของอาการ "การ์ดขึ้น 0%" ที่ไล่ข้อมูลกันไว้รอบก่อน)
   *
   * ตอนยกเลิกการเลือกไม่ต้องเลื่อน — เด็กกำลังดูรายการหัวข้ออยู่
   */
  function choose(id: string): void {
    setPicked((cur) => (cur === id ? null : id));
  }

  // ★ เลื่อนใน effect ไม่ใช่ใน onClick — ถ้าสั่งเลื่อนใน handler จะเกิด **ก่อน** React
  //   วาดใหม่ ซึ่งเป็นตอนที่การ์ดบนสุดยังมีเนื้อหาเดิมและการ์ดชุดรวมยังไม่มีบรรทัด
  //   "กำลังเลือกหัวข้ออยู่" ความสูงของหน้าจึงเปลี่ยนหลังจากนั้นแล้วจอไหลผิดที่
  //   (วัดบน production จริง: scrollY 955 → 1069 คือเลื่อน **ลง** แทนที่จะขึ้น)
  useEffect(() => {
    if (picked === null) return; // ยกเลิกการเลือกไม่ต้องเลื่อน เด็กกำลังดูรายการอยู่
    wholeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [picked]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const subjects = useQuery({
    queryKey: ["subjects"],
    queryFn: () => api.get<SubjectOut[]>("/catalog/subjects"),
  });
  const subject = subjects.data?.find((s) => s.code === code);

  const tree = useQuery({
    queryKey: ["topics", subject?.id],
    enabled: Boolean(subject),
    queryFn: () =>
      api.get<TopicTree>(
        `/catalog/topics${gradeQ}subject_id=${subject?.id ?? ""}`,
      ),
  });

  const topics = tree.data ? flatten(tree.data.items) : [];
  const min = tree.data?.min_questions_per_topic ?? 5;
  const thin = topics.filter((n) => !n.selectable);
  const pickedNode = picked ? topics.find((n) => n.id === picked) : undefined;

  async function start(
    count: number | typeof ALL,
    topicId: string | null = picked,
  ): Promise<void> {
    if (!subject || !learnerId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const quiz = await api.post<QuizOut>("/quizzes", {
        learner_id: learnerId,
        mode: topicId ? "by_topic" : "by_subject",
        topic_ids: topicId ? [topicId] : undefined,
        subject_id: topicId ? undefined : subject.id,
        delivery_mode: mode,
        count: count === ALL ? undefined : count,
        all_questions: count === ALL,
        shuffle_choices: true,
      });
      navigate(`/quiz/${quiz.id}`, {
        state: { requested: quiz.requested_count, actual: quiz.actual_count },
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === "VALIDATION_ERROR") {
        setError(e.message || t("error.topicTooThin"));
      } else {
        setError(e instanceof ApiError ? e.message : t("error.network"));
      }
    } finally {
      setBusy(false);
    }
  }

  const who = kid ? { label: kid.grade_label, name: kid.nickname } : undefined;
  // จำนวนข้อจริงในขอบเขตที่เลือกอยู่ (หัวข้อแม่นับรวมลูกแล้วจากเซิร์ฟเวอร์)
  const total = pickedNode
    ? pickedNode.question_count
    : (subject?.published_question_count ?? 0);
  const counts = COUNTS.filter((c) => c < total);
  const highlighted = counts.includes(10) ? 10 : counts[0];

  // ปุ่มจำนวนข้อ (กติกา D21) — ใช้กับการ์ดชุดรวม (D22) · การ์ดวิชา/หัวข้อที่เลือกยังใช้บล็อกเดิมด้านล่าง
  function countButtons(n: number, topicId: string): JSX.Element {
    const visible = COUNTS.filter((c) => c < n);
    const main = visible.includes(10) ? 10 : visible[0];
    // ★ มีหัวข้อถูกเลือกอยู่ = กดปุ่มชุดคละไม่ได้ (24 ก.ย. 2569)
    //   "บอกว่ากดผิด" ไม่พอ ต้องกดผิดไม่ได้เลย — ปุ่มพวกนี้อยู่ตรงหน้าเด็กพอดี
    //   ตอนที่เพิ่งแตะเลือกหัวข้อ ซึ่งเป็นจังหวะที่กดพลาดง่ายที่สุด
    const locked = picked !== null;
    return (
      <div className={`btns${locked ? " locked" : ""}`}>
        {visible.map((c) => (
          <button
            key={c}
            type="button"
            className={`bk-pill${c === main ? "" : " ghost"}`}
            disabled={busy || !learnerId || locked}
            onClick={() => void start(c, topicId)}
          >
            {t("book.nQuestions", "th", { n: c })}
          </button>
        ))}
        <span className="all" data-testid="all">
          <button
            type="button"
            className="bk-pill ghost"
            disabled={busy || !learnerId || n === 0 || locked}
            onClick={() => void start(ALL, topicId)}
          >
            {t("book.allQuestions", "th", { n })}
          </button>
          {n > ESTIMATE_FROM ? (
            <small data-testid="all-estimate">
              {t("book.allEstimate", "th", { m: estimateMinutes(n) })}
            </small>
          ) : null}
        </span>
        {locked ? (
          <p className="bk-locked" data-testid="bundle-locked">
            {t("book.bundleLocked")}
          </p>
        ) : null}
      </div>
    );
  }

  if (subjects.isLoading || tree.isLoading) {
    return (
      <Shell here={t("topic.pick")} who={who}>
        <Center>
          <p>{t("state.loading")}</p>
        </Center>
      </Shell>
    );
  }

  if (!subject) {
    return (
      <Shell who={who}>
        <Center>
          <p>{t("error.notFound")}</p>
        </Center>
      </Shell>
    );
  }

  const ck = colorClass(subject.color_key);
  const bands = tree.data ? bandsOf(tree.data.items, subject.name_th) : [];
  // D22 — หัวข้อแม่ที่ลูกทุกตัวเป็นการ์ด (ไม่มีหลาน) · ไม่มีข้อผูกตรง · รวมลูกแล้ว **น้อยกว่า** ทั้งวิชา
  //   (เท่ากัน = การ์ดวิชาให้ตัวเลขเดียวกันอยู่แล้ว ไม่แสดง — สังคม/ไทย ป.4 จึงเหมือนเดิม)
  const bundles = topics.filter(
    (n) =>
      n.children.length > 0 &&
      n.children.every((c) => c.children.length === 0) &&
      (n.direct_question_count ?? 0) === 0 &&
      n.question_count < subject.published_question_count,
  );
  // D25 — จัดแถบเป็นกลุ่มตามหัวข้อแม่ แล้ววางการ์ดชุดรวมของแม่ไว้หัวกลุ่มของตัวเอง
  //   เดิมการ์ดชุดรวมลอยอยู่เหนือแถบแรกที่มีลูกของมัน ซึ่งพอมีสองใบเด็กแยกไม่ออกว่าใบไหนของอะไร
  const groups: Array<{
    ownerId: string | null;
    ownerName: string | null;
    bands: typeof bands;
    bundles: TopicNode[];
  }> = [];
  for (const band of bands) {
    const last = groups[groups.length - 1];
    if (last && last.ownerId === band.ownerId) last.bands.push(band);
    else
      groups.push({
        ownerId: band.ownerId,
        ownerName: band.ownerName,
        bands: [band],
        bundles: [],
      });
  }
  for (const b of bundles) {
    const kids = new Set(b.children.map((c) => c.id));
    const group =
      groups.find((g) => g.ownerId === b.id) ??
      groups.find((g) =>
        g.bands.some((x) => x.units.some((u) => kids.has(u.id))),
      );
    if (group) group.bundles.push(b);
  }
  // ขึ้นหัวคั่นเฉพาะเมื่อวิชานี้มีหัวข้อแม่มากกว่าหนึ่ง — วิชาที่มีเล่มเดียวหน้าตาเหมือนเดิมทุกพิกเซล
  const showOwners = new Set(groups.map((g) => g.ownerId)).size > 1;
  // ป้าย "ชุดรวม N บท/หน่วย/เรื่อง · M หัวข้อ" — คำหน่วยนับมาจากคำนำหน้าของ unit_label (lib/book.bundleUnits)
  //   ป้ายรูปแบบอื่น/ไม่มีป้าย → "ชุดรวม · M หัวข้อ" ไม่นับ
  const bundleTag = (b: TopicNode): string => {
    const u = bundleUnits(b.children);
    return u
      ? t("book.bundleTag", "th", {
          n: u.n,
          word: u.word,
          topics: b.children.length,
        })
      : t("book.bundleTagPlain", "th", { topics: b.children.length });
  };

  return (
    <Shell here={subject.name_th} onQuit={() => navigate("/learn")} who={who}>
      <h1>{t("book.title")}</h1>
      <p className="sub">
        {kid ? `${kid.grade_label} · ${t("book.pickTopic")}` : ""}
      </p>

      {subjects.data ? (
        <SubjectStrip subjects={subjects.data} current={subject.code} />
      ) : null}

      {error ? <p className="err">{error}</p> : null}

      {/* ─── ทำทั้งวิชา / ทำหัวข้อที่เลือก — ปุ่มจำนวนข้อกดแล้วเริ่มทันที ─── */}
      <div className={`bk-whole ${ck}`} data-testid="whole" ref={wholeRef}>
        <div>
          <div className="t">
            {pickedNode
              ? t("book.topicTitle", "th", { topic: pickedNode.name })
              : t("book.wholeTitle", "th", { subject: subject.name_th })}
          </div>
          <div className="d">
            {pickedNode
              ? t("book.topicDesc", "th", { n: pickedNode.question_count })
              : t("book.wholeDesc", "th", {
                  n: subject.published_question_count,
                })}
          </div>
          <div
            className="bk-modes"
            role="group"
            aria-label={t("topic.modeHead")}
          >
            <button
              type="button"
              className={`bk-pill small${mode === "practice" ? "" : " ghost"}`}
              aria-pressed={mode === "practice"}
              onClick={() => setMode("practice")}
            >
              {t("book.modePractice")}
            </button>
            <button
              type="button"
              className={`bk-pill small${mode === "exam" ? "" : " ghost"}`}
              aria-pressed={mode === "exam"}
              onClick={() => setMode("exam")}
            >
              {t("book.modeExam")}
            </button>
          </div>
        </div>
        <div className="btns">
          {counts.map((c) => (
            <button
              key={c}
              type="button"
              className={`bk-pill${c === highlighted ? "" : " ghost"}`}
              disabled={busy || !learnerId}
              onClick={() => void start(c)}
            >
              {t("book.nQuestions", "th", { n: c })}
            </button>
          ))}
          <span className="all" data-testid="all">
            <button
              type="button"
              className="bk-pill ghost"
              disabled={busy || !learnerId || total === 0}
              onClick={() => void start(ALL)}
            >
              {t("book.allQuestions", "th", { n: total })}
            </button>
            {total > ESTIMATE_FROM ? (
              <small data-testid="all-estimate">
                {t("book.allEstimate", "th", { m: estimateMinutes(total) })}
              </small>
            ) : null}
          </span>
        </div>
      </div>

      {/* ─── หัวคั่นหัวข้อแม่ (D25) → แถบสาระ → การ์ดหน่วย ─── */}
      {groups.map((group) => (
        <div key={group.ownerId ?? "-"} data-testid="owner-group">
          {showOwners && group.ownerName ? (
            <h2 className={`bk-owner ${ck}`} data-testid="owner">
              {group.ownerName}
            </h2>
          ) : null}
          {group.bundles.map((b) => {
            const pct = b.progress?.pct;
            return (
              <div
                key={b.id}
                className={`bk-whole bk-bundle ${ck}`}
                data-testid="bundle"
                data-topic-id={b.id}
              >
                <div>
                  <div className="t">{b.name}</div>
                  <div className="d">{bundleTag(b)}</div>
                  <div className="meta">
                    <span className="bk-bar" aria-hidden="true">
                      <i style={{ width: `${pct ?? 0}%` }} />
                    </span>
                    {pct === undefined ? (
                      <span className="bk-chip new">{t("book.never")}</span>
                    ) : (
                      <>
                        <span className="bk-score">{pct}%</span>
                        {pct === 100 ? (
                          <span className="bk-chip">{t("book.mastered")}</span>
                        ) : null}
                        <span className="bk-frac" data-testid="frac">
                          {t("book.frac", "th", {
                            c: b.progress!.correct,
                            n: b.progress!.total,
                          })}
                        </span>
                      </>
                    )}
                    <span>
                      {t("topic.count", "th", { n: b.question_count })}
                    </span>
                  </div>
                </div>
                {countButtons(b.question_count, b.id)}
              </div>
            );
          })}
          {group.bands.map((band) => {
            let ordinal = 0;
            return (
              <section
                key={`${group.ownerId ?? "-"}/${band.section}`}
                data-testid="band"
              >
                <div className={`bk-band ${ck}`}>
                  <h3>{band.section}</h3>
                  <span className="n">
                    {t("book.bandCount", "th", {
                      topics: band.units.length,
                      questions: band.questions,
                    })}
                  </span>
                </div>
                <div className="bk-units">
                  {band.units.map((n) => {
                    ordinal += 1;
                    const tab = unitTab(
                      n.unit_label,
                      ordinal,
                      t("book.unitWord"),
                    );
                    const pct = n.progress?.pct;
                    const isPicked = picked === n.id;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        className={`bk-unit ${ck}`}
                        data-topic-id={n.id}
                        data-depth={n.depth}
                        disabled={!n.selectable}
                        aria-current={isPicked ? "true" : undefined}
                        onClick={() => choose(n.id)}
                      >
                        <span className="tab">
                          {tab.small ? <small>{tab.small}</small> : null}
                          <b className={tab.numeric ? undefined : "txt"}>
                            {tab.big}
                          </b>
                        </span>
                        <span className="body">
                          <span className="ttl">{n.name}</span>
                          <span className="meta">
                            <span className="bk-bar" aria-hidden="true">
                              <i style={{ width: `${pct ?? 0}%` }} />
                            </span>
                            {!n.selectable ? (
                              <span className="bk-chip thin">
                                {t("book.thin")}
                              </span>
                            ) : pct === undefined ? (
                              <span className="bk-chip new">
                                {t("book.never")}
                              </span>
                            ) : (
                              <>
                                <span className="bk-score">{pct}%</span>
                                {pct === 100 ? (
                                  <span className="bk-chip">
                                    {t("book.mastered")}
                                  </span>
                                ) : null}
                                <span className="bk-frac" data-testid="frac">
                                  {t("book.frac", "th", {
                                    c: n.progress!.correct,
                                    n: n.progress!.total,
                                  })}
                                </span>
                              </>
                            )}
                            <span>
                              {t("topic.count", "th", { n: n.question_count })}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      ))}

      {thin.length ? (
        <div className="note" style={{ marginTop: 22 }}>
          {t("topic.thinNote", "th", { n: thin.length, min })}
        </div>
      ) : null}
    </Shell>
  );
}
